"use client";
import Link from "@/components/common/DashboardLink";
import Image from "@/components/AppImage";
import { PaylioContext } from "@/context/context";
import { useReceiveFunding } from "@/hooks/useReceiveFunding";
import {
  formatBankCopyText,
  getReceiveFundingPdfUrl,
} from "@/services/receiveFundingApi";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  BsBank2,
  BsBullseye,
  BsCheck2Circle,
  BsCopy,
  BsDownload,
  BsLightningChargeFill,
  BsShare,
} from "react-icons/bs";
import support_icon from "/public/images/icon/support-icon.png";

function DetailField({ label, value }) {
  return (
    <div className="fp-receive-field">
      <span className="fp-receive-field__label">{label}</span>
      <p className="fp-receive-field__value">{value ?? "—"}</p>
    </div>
  );
}

function ReceiveSkeleton() {
  return (
    <div className="fp-receive" aria-busy="true">
      <div className="fp-receive__skeleton" style={{ height: 28, width: "55%", marginBottom: 12 }} />
      <div className="fp-receive__skeleton" style={{ height: 16, width: "80%", marginBottom: 28 }} />
      {[1, 2, 3].map((k) => (
        <div key={k} className="fp-receive__card">
          <div className="fp-receive__skeleton" style={{ height: 12, width: "40%", marginBottom: 16 }} />
          <div className="fp-receive__skeleton" style={{ height: 120, width: "100%" }} />
        </div>
      ))}
    </div>
  );
}

const StepOne = () => {
  const { activeLefMenu } = useContext(PaylioContext);
  const { data, loading, error, refetch } = useReceiveFunding();
  const [copyHint, setCopyHint] = useState("");

  const bank = data?.bank;
  const crypto = data?.crypto;

  const copyText = useMemo(() => formatBankCopyText(bank), [bank]);

  const pdfHref = useMemo(() => getReceiveFundingPdfUrl(data), [data]);

  const handleCopy = useCallback(async () => {
    if (!copyText) return;
    try {
      await navigator.clipboard.writeText(copyText);
      setCopyHint("Copied");
      setTimeout(() => setCopyHint(""), 2000);
    } catch {
      setCopyHint("Copy failed");
      setTimeout(() => setCopyHint(""), 2000);
    }
  }, [copyText]);

  const handleShare = useCallback(async () => {
    if (!copyText) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "FlowPay virtual bank details",
          text: copyText,
        });
      } else {
        await navigator.clipboard.writeText(copyText);
        setCopyHint("Copied for sharing");
        setTimeout(() => setCopyHint(""), 2000);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setCopyHint("Share unavailable");
        setTimeout(() => setCopyHint(""), 2000);
      }
    }
  }, [copyText]);

  return (
    <section
      className={`dashboard-section receive-flowpay ${
        activeLefMenu ? "body-collapse" : ""
      }`}>
      <div className="overlay pt-120">
        <div className="container-fruid">
          <div className="main-content">
            <div className="head-area d-flex align-items-center justify-content-between mb-0">
              <h4 className="fp-receive__title mb-0">Receive USD</h4>
              <div className="icon-area">
                <Image src={support_icon} alt="" />
              </div>
            </div>
            <p className="fp-receive__subtitle">
              ACH and wire to your US virtual account — converted and sent on-chain automatically.
            </p>

            {loading ? (
              <ReceiveSkeleton />
            ) : (
              <div className="fp-receive">
                {error ? (
                  <div className="fp-receive__alert" role="alert">
                    <span>{error} Showing last cached defaults until the connection succeeds.</span>
                    <button type="button" onClick={() => refetch()}>
                      Retry
                    </button>
                  </div>
                ) : null}
                {copyHint ? (
                  <p className="small text-success mb-2" style={{ color: "#34d399" }}>
                    {copyHint}
                  </p>
                ) : null}

                <div className="fp-receive__card">
                  <div className="fp-receive__section-kicker">Your US virtual bank account</div>
                  <div className="fp-receive__bank-inner">
                    <div className="fp-receive__grid">
                      <DetailField label="Account name" value={bank?.accountName} />
                      <DetailField label="Bank name" value={bank?.bankName} />
                      <DetailField label="Routing number" value={bank?.routingNumber} />
                      <DetailField label="Account number" value={bank?.accountNumber} />
                      <DetailField label="Account type" value={bank?.accountType} />
                      <DetailField label="Supports" value={bank?.supportsLabel} />
                    </div>
                  </div>
                  <div className="fp-receive__actions">
                    <button type="button" className="fp-receive__btn" onClick={handleCopy}>
                      <BsCopy size={18} aria-hidden />
                      Copy details
                    </button>
                    <button type="button" className="fp-receive__btn" onClick={handleShare}>
                      <BsShare size={18} aria-hidden />
                      Share
                    </button>
                    {pdfHref ? (
                      <a
                        className="fp-receive__btn fp-receive__btn--primary text-decoration-none"
                        href={pdfHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        download>
                        <BsDownload size={18} aria-hidden />
                        Download PDF
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="fp-receive__btn fp-receive__btn--primary"
                        disabled
                        title="Your API can return pdfDownloadPath or expose GET /receive/funding/pdf">
                        <BsDownload size={18} aria-hidden />
                        Download PDF
                      </button>
                    )}
                  </div>
                </div>

                <div className="fp-receive__card">
                  <div className="fp-receive__section-kicker">What happens when USD arrives</div>
                  <div className="fp-receive__timeline">
                    <div className="fp-receive-step">
                      <div className="fp-receive-step__icon">
                        <BsBank2 aria-hidden />
                      </div>
                      <div>
                        <h3 className="fp-receive-step__title">USD received</h3>
                        <p className="fp-receive-step__text">
                          Clients send ACH or wire to your virtual account. Funds post to the FlowPay
                          ledger when the bank clears them.
                        </p>
                      </div>
                    </div>
                    <div className="fp-receive-step">
                      <div className="fp-receive-step__icon">
                        <BsLightningChargeFill aria-hidden />
                      </div>
                      <div>
                        <h3 className="fp-receive-step__title">Instant conversion</h3>
                        <p className="fp-receive-step__text">
                          FlowPay converts incoming USD to your chosen asset at a live market rate so
                          value moves through without idle float.
                        </p>
                      </div>
                    </div>
                    <div className="fp-receive-step">
                      <div className="fp-receive-step__icon">
                        <BsBullseye aria-hidden />
                      </div>
                      <div>
                        <h3 className="fp-receive-step__title">Dispatched on-chain</h3>
                        <p className="fp-receive-step__text">
                          Crypto is sent to your destination wallet. FlowPay does not custody balances
                          on your behalf after dispatch.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fp-receive__card">
                  <div className="fp-receive__section-kicker">Destination wallet</div>
                  <label className="fp-receive-field__label" htmlFor="fp-dest-address">
                    Current destination address
                  </label>
                  <div id="fp-dest-address" className="fp-receive__input" role="textbox" aria-readonly>
                    {crypto?.destinationAddress ?? "—"}
                  </div>
                  <label className="fp-receive-field__label" htmlFor="fp-dest-asset">
                    Convert to
                  </label>
                  <div id="fp-dest-asset" className="fp-receive__input fp-receive__input--asset">
                    {crypto?.assetLabel ?? "—"}
                  </div>
                  <div className="fp-receive__notice">
                    <BsCheck2Circle className="fp-receive__notice-icon" aria-hidden />
                    <span className="fp-receive__notice-text">
                      All incoming USD is converted and sent here automatically. Change this in
                      Crypto settings.
                    </span>
                  </div>
                  <Link
                    href={crypto?.settingsHref || "/crypto"}
                    className="fp-receive__link">
                    Edit in Crypto settings
                    <span className="fp-receive__link-arrow" aria-hidden>
                      →
                    </span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StepOne;
